import React, { useState } from "react";

import { ConfirmModal, ContextMenu } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useContextMenus } from "@/hooks/useContextMenus";
import { useHomeLocation } from "@/hooks/useHomeLocation";
import { useHomeNavigation } from "@/hooks/useHomeNavigation";
import { usePlanGate } from "@/hooks/usePlanGate";
import { useProjectActions } from "@/hooks/useProjectActions";

import { UnsavedChangesModal } from "@/components/organisms";

import { isSup } from "@/utils/URoles";

import { ResourceRole } from "@/constants/CRoles";

import { TProject } from "@/types";

export const ProjectsCtxMenu: React.FC = () => {
	const { taskId, isEditing, projectId: activeProjectId } = useHomeLocation();
	const { toNewTask, toAll } = useHomeNavigation();
	const pdf = usePlanGate("pdf");
	const { archive, remove } = useProjectActions();
	const { projects: menu } = useContextMenus();
	const [showConfirmModal, setShowConfirmModal] = useState<TProject>();
	const navigate = useNavigate();

	const [showUnsavedChangesModal, setShowUnsavedChangesModal] =
		useState<TProject>();

	const handleEdit = (project: TProject) => {
		navigate(`/projects/${project.id}?tab=settings`);
	};

	const handleRemove = (project?: TProject, confirmed = false) => {
		if (!confirmed) {
			setShowConfirmModal(project);
			return;
		}

		return remove(project!).then(() => {
			// The home page was filtered on it: back to the whole view.
			if (activeProjectId === project!.id) toAll();
		});
	};

	const handleCreateNewTask = (project?: TProject, fromConfirm = false) => {
		if (!project) return;

		if (!fromConfirm && taskId && isEditing) {
			setShowUnsavedChangesModal(project);
			return;
		}
		toNewTask({ projectId: project.id });
	};

	// The active project is read from the view's project list, which
	// archiving invalidates: the header and this menu follow on their own.
	const handleArchive = (project: TProject) => archive(project);

	return (
		<div>
			<ContextMenu
				menu={menu}
				items={(project) => [
					{
						label: t("actions.edit"),
						picto: "edit",
						// Archived: read-only until unarchived (the API refuses it).
						hidden:
							project.isArchived ||
							!isSup(ResourceRole.MAINTAINER, project.role),
						onClick: () => handleEdit(project),
					},
					{
						label: t("tasks.newTask"),
						picto: "addCircle",
						hidden:
							project.isArchived ||
							!isSup(ResourceRole.USER, project.role),
						onClick: () => handleCreateNewTask(project),
					},
					{
						label: t("reports.generate"),
						// A plan feature, not a role: off-plan it locks and
						// opens the upgrade modal (the API refuses it anyway).
						picto: pdf.locked ? "lock" : "briefcase",
						onClick: pdf.locked
							? pdf.open
							: () =>
									navigate(
										`/reports/new?projectId=${project.id}`
									),
					},
					{
						label: t("actions.infos"),
						picto: "info",
						onClick: () => navigate(`/projects/${project.id}`),
					},
					{
						label: t("actions.archive"),
						picto: "archive",
						hidden:
							project.isArchived ||
							!isSup(ResourceRole.ADMIN, project.role),
						onClick: () => handleArchive(project),
					},
					{
						label: t("actions.unarchive"),
						picto: "archive",
						hidden:
							!project.isArchived ||
							!isSup(ResourceRole.ADMIN, project.role),
						onClick: () => handleArchive(project),
					},
					{
						label: t("actions.delete"),
						picto: "trash",
						color: "danger",
						hidden:
							project.isArchived ||
							!isSup(ResourceRole.ADMIN, project.role),
						onClick: () => handleRemove(project),
					},
				]}
			/>
			<ConfirmModal
				open={!!showConfirmModal}
				onClose={() => {
					setShowConfirmModal(undefined);
				}}
				onConfirm={() => {
					handleRemove(showConfirmModal, true);
					setShowConfirmModal(undefined);
				}}
				title={t("projects.confirmDelete.title")}
				confirmText={t("actions.delete")}
				cancelText={t("actions.cancel")}
			>
				<Trans
					i18nKey="projects.confirmDelete.message"
					values={{
						name: showConfirmModal?.name,
					}}
					// allow bold tag
					components={{ b: <b /> }}
				/>
			</ConfirmModal>
			<UnsavedChangesModal
				open={!!showUnsavedChangesModal}
				onClose={() => {
					setShowUnsavedChangesModal(undefined);
				}}
				onConfirm={() => {
					handleCreateNewTask(showUnsavedChangesModal, true);
					setShowUnsavedChangesModal(undefined);
				}}
			/>
		</div>
	);
};
